import {WIDTH, HEIGHT, dimensions, validRod} from './model.mjs';

export const MIN_ROD_UNIT = 26.4;
const EPSILON = 1e-6;
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));

// This is display metadata, never a migration of authored coordinates.
export function activityRodBounds(document, additionalRods = []) {
  const rods = [...additionalRods];
  for (const segment of document.problems || []) {
    rods.push(...(segment.setup || []), ...(segment.answer || []));
    for (const frame of segment.demo || []) rods.push(...(frame.rods || []));
    for (const answer of segment.incorrectAnswers || []) rods.push(...(answer.rods || []));
    rods.push(...(segment.incorrectAnswer?.rods || []));
  }
  const valid = rods.filter(validRod);
  if (!valid.length) return null;
  return valid.reduce((bounds, rod) => {
    const [width, height] = dimensions(rod);
    return {left: Math.min(bounds.left, rod.x), top: Math.min(bounds.top, rod.y),
      right: Math.max(bounds.right, rod.x + width), bottom: Math.max(bounds.bottom, rod.y + height)};
  }, {left: WIDTH, top: HEIGHT, right: 0, bottom: 0});
}

export function fitRodViewport({width, height, bounds = null, preferredY = 0, minUnit = MIN_ROD_UNIT}) {
  const availableWidth = width;
  const minimumRows = Math.max(1, bounds ? bounds.bottom - bounds.top : 1);
  // Height can cap the grid width, but rods never shrink below the dispenser.
  const unit = Math.max(minUnit, Math.min(availableWidth / WIDTH, height / minimumRows));
  // Paint the grid across the whole workspace, including the spare area on
  // a wide screen. Saved coordinates retain their 30-column logical system.
  const columns = unit > 0 ? Math.max(0, Math.floor((availableWidth + EPSILON) / unit)) : 0;
  width = columns * unit;
  const rows = unit > 0 ? Math.max(0, Math.min(HEIGHT, Math.floor((height + EPSILON) / unit))) : 0;
  const requiredWidth = WIDTH * minUnit;
  const requiredHeight = minimumRows * Math.max(minUnit, unit);
  const base = {unit, rows, columns, x: 0, y: 0, width, height, availableWidth, requiredWidth, requiredHeight};
  if (!Number.isFinite(width) || !Number.isFinite(height) || availableWidth + EPSILON < requiredWidth || rows < minimumRows) {
    return {...base, ok: false, reason: availableWidth + EPSILON < requiredWidth ? 'width' : 'height'};
  }
  const minY = Math.max(0, (bounds?.bottom || 0) - rows);
  const maxY = Math.min(bounds?.top ?? HEIGHT, HEIGHT - rows);
  if (minY > maxY) return {...base, ok: false, reason: 'height'};
  return {...base, y: clamp(Math.round(preferredY), minY, maxY), ok: true};
}

export function pointToLogical(view, x, y) {
  const logicalX = view.chart
    ? x < view.width / 2 ? Math.min(x / view.unit, WIDTH / 2 - 1) : WIDTH / 2 + (x - view.width / 2) / view.unit
    : x / view.unit;
  return {x: view.x + logicalX, y: view.y + y / view.unit};
}
export function pointToScreen(view, x, y) {
  const chartOffset = view.chart && x >= WIDTH / 2 ? view.width / 2 - WIDTH / 2 * view.unit : 0;
  return {x: (x - view.x) * view.unit + chartOffset, y: (y - view.y) * view.unit};
}
export function rodInViewport(view, rod) {
  if (!view?.ok || !validRod(rod, {columns:view.columns})) return false;
  const [width, height] = dimensions(rod);
  return rod.x >= view.x && rod.y >= view.y && rod.x + width <= view.x + view.columns && rod.y + height <= view.y + view.rows;
}
export function constrainRodToViewport(view, rod, x, y) {
  if (!view?.ok) return null;
  const [width, height] = dimensions(rod);
  if (width > view.columns || height > view.rows) return null;
  return {...rod, x: clamp(Math.round(x), view.x, view.x + view.columns - width),
    y: clamp(Math.round(y), view.y, Math.min(HEIGHT, view.y + view.rows) - height)};
}

// Scale the complete frame together, preserving the menu's geometry and the
// ratio between dispenser rods and canvas rods. The camera still uses the
// original authored coordinates, and the same frame serves every segment.
export function fitMathRodsFrame({width, height, promptHeight = 128}) {
  const minimumWidth = 216 + 26 + 38 + WIDTH * MIN_ROD_UNIT;
  // Reserve the complete grid, including space for later learner placements.
  // Resizing or changing segments must never hide previously placed rods.
  const minimumRows = HEIGHT;
  const minimumHeight = Math.max(516, promptHeight + 30 + 28 + 18 + 40 + minimumRows * MIN_ROD_UNIT);
  const scale = Math.max(.01, Math.min(1, width / minimumWidth, height / minimumHeight));
  return {scale, width: width / scale, height: height / scale, minimumWidth, minimumHeight};
}
